import { useQuery } from "@tanstack/react-query";
import {
  authenticatedGet,
  authenticatedPost,
  authenticatedDelete,
} from "../../api/authenticatedFetch";

const BASE = "/api/v1";

// Role codes that only make sense scoped to a slice of data (cost_owner /
// tech_cost_owner) — the backend rejects assigning either to a user with zero
// scope grants (SCOPE_REQUIRED, see backend_go internal/users/errors.go and
// isScopedRoleCode). Centralized here so the create/detail assignment UIs
// agree on which codes require a data-scope check.
export const SCOPE_ONLY_ROLE_CODES = ["cost_owner", "tech_cost_owner"];

/**
 * Whether a role needs a data-scope grant before it can be assigned to a
 * user with zero scope grants — either directly (its own code is one of
 * SCOPE_ONLY_ROLE_CODES) or by lineage (an organisation role cloned from one
 * of them). Mirrors the backend's lineage-aware isScopedRoleCode check (see
 * backend_go internal/users — Sub-project E1 Task 3) so an organisation role
 * derived from cost_owner/tech_cost_owner still surfaces the scope-required
 * hint here, even though its own code no longer matches the list.
 *
 * @param {{ code?: string, cloned_from?: string|null }|undefined|null} role
 */
export function isScopedRole(role) {
  if (!role) return false;
  if (SCOPE_ONLY_ROLE_CODES.includes(role.code)) return true;
  return !!role.cloned_from && SCOPE_ONLY_ROLE_CODES.includes(role.cloned_from);
}

// --- Data-scope grants ---
//
// A scope grant restricts which slice of organisation/product/use-case data a
// user can see. Each grant has a label and one or more clauses; clauses
// within a grant are AND'd together (all must match). See
// backend_go cmd/api/scope_grants_handlers.go for the source of truth.

/**
 * @param {number|string} userId
 * @returns {Promise<Array<{id: number|string, label: string, clauses: Array<{dimensionCode: string, operator: string, nodeId: number, nodeLabel?: string}>}>>}
 */
export const listUserScopeGrants = async (userId) => {
  const res = await authenticatedGet(`${BASE}/users/${userId}/scope-grants`);
  return res?.scope_grants ?? [];
};

/**
 * @param {number|string} userId
 * @param {{ label: string, clauses: Array<{dimensionCode: string, operator: string, nodeId: number}> }} body
 */
export const createUserScopeGrant = async (userId, body) => {
  const res = await authenticatedPost(`${BASE}/users/${userId}/scope-grants`, body);
  return res?.grant ?? res;
};

/**
 * @param {number|string} userId
 * @param {number|string} grantId
 */
export const deleteUserScopeGrant = async (userId, grantId) => {
  return authenticatedDelete(`${BASE}/users/${userId}/scope-grants/${grantId}`);
};

// --- Budget dimensions (feeds the dimension + subtree picker) ---

export const listBudgetDimensions = async () => {
  const res = await authenticatedGet(`${BASE}/budget-dimensions`);
  const raw = res?.budget_dimensions ?? [];
  // BudgetDimension is a Go domain struct and may serialize PascalCase
  // (ID/Code/Name) instead of the json-tagged lowercase fields — normalize
  // defensively rather than hardcoding one shape.
  return raw.map((d) => ({
    id: d.id ?? d.ID,
    code: d.code ?? d.Code ?? "",
    name: d.name ?? d.Name ?? d.Code ?? d.code ?? "",
  }));
};

// --- react-query hooks ---

// Query key shared with the component so it can invalidate after a
// create/delete mutation (see UserScopeGrants.jsx).
export const scopeGrantsKey = (userId) => ["user-scope-grants", userId];

export function useUserScopeGrants(userId) {
  return useQuery({
    queryKey: scopeGrantsKey(userId),
    queryFn: () => listUserScopeGrants(userId),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}
